<template>
  <tr
    v-if="!showIfBookmarked || (showIfBookmarked && isBookmarked)"
    class="md:border-b-2"
    :class="{ '!bg-yellow-200': isOutbid }"
    :style="{ 'background-color': productToColor(productMap[lot.productUuid].code)}"
  >
    <td>
      <BookmarkIcon
        :lot="lot"
        :is-bookmarked="isBookmarked"
        @added="isBookmarked = true"
        @removed="isBookmarked = false"
      />
    </td>
    <td class="text-center font-bold">
      {{ lot.code }}<br>
      <span
        :title="lot.wasteCode"
        :style="{ color: stc(lot.wasteCode) }"
      >
        {{ productMap[lot.productUuid].code }} - {{ JSON.parse(productMap[lot.productUuid].multilangName)[$i18n.locale] }}
      </span>
    </td>
    <td class="text-center">
      {{ lot.quantity }} t
    </td>
    <td class="text-center">
      {{ lot.currentPrice }} €/t
    </td>
    <td
      v-if="auction.auctionStatus == 'REDISTRIBUTING_1' || auction.auctionStatus == 'REDISTRIBUTING_2' || auction.auctionStatus == 'CLOSED'"
      class="text-center"
    >
      {{ lot.assignedQuantity }} t
    </td>
    <td
      v-if="auction.auctionStatus == 'OPEN' || auction.auctionStatus == 'PUBLISHED'"
      class="text-center"
    >
      {{ dateUtils.format(lot.endAt) }}
    </td>
    <td
      v-if="auction.auctionStatus == 'OPEN' || auction.auctionStatus == 'PUBLISHED'"
      class="text-center"
    >
      <CircleCountdown
        v-if="auction.auctionStatus == 'OPEN'"
        :start="auction.startAt"
        :end="lot.endAt"
        :only-text="true"
        :with-remaining-label="false"
      />
    </td>
    <td
      v-if="auction.auctionStatus == 'OPEN'"
      class="text-center hidden md:table-cell"
    >
      <BidButton
        v-if="authStore.canBid && myLot?.canBid && !lot.closed"
        :auction="auction"
        :lot="lot"
        :my-lot="myLot"
        @click.prevent="biddingLot = lot"
      />

      <a
        v-else-if="auction?.me?.lots[lot.uuid].winner"
        class="btn btn-winner"
      >
        {{ $t("auction.winning") }}
      </a>
    </td>
    <td
      v-if="auction.auctionStatus == 'OPEN'"
      class="text-center"
    >
      {{ lot.bidCount }}
    </td>

    <td
      v-if="auction.auctionStatus == 'REDISTRIBUTING_1' || auction.auctionStatus == 'REDISTRIBUTING_2' || auction.auctionStatus == 'CLOSED'"
      class="text-center hidden md:table-cell"
    >
      <a
        v-if="auction?.me?.lots[lot.uuid].winner"
        class="btn btn-winner"
      >
        {{ $t("auction.winning") }}
      </a>
    </td>

    <td class="text-center">
      <WarehouseButton
        @click.prevent="warehousesModal = true"
      />
      <AttributesButton @click.prevent="attributesModal = true" />
      <BidHistoryButton @click.prevent="historyModal = true" />
    </td>
  </tr>

  <tr
    v-if="!showIfBookmarked || (showIfBookmarked && isBookmarked)"
    class="md:hidden border-b-2"
    :style="{ 'background-color': productToColor(productMap[lot.productUuid].code)}"
    :class="{ '!bg-yellow-200': isOutbid }"
  >
    <td
      colspan="100"
      class="pb-4"
    >
      <div
        v-if="auction.auctionStatus == 'OPEN'"
        class="flex"
      >
        <div class="flex-grow" />
        <div>
          <BidButton
            v-if="authStore.canBid && myLot?.canBid && !lot.closed"
            :auction="auction"
            :lot="lot"
            :my-lot="myLot"
            @click.prevent="biddingLot = lot"
          />
          <a
            v-else-if="auction?.me?.lots[lot.uuid].winner"
            class="btn btn-winner"
          >
            {{ $t("auction.winning") }}
          </a>
        </div>
      </div>

      <div
        v-else-if="auction.auctionStatus == 'REDISTRIBUTING_1' || auction.auctionStatus == 'REDISTRIBUTING_2' || auction.auctionStatus == 'CLOSED'"
        class="flex"
      >
        <div class="flex-grow" />
        <div>
          <a
            v-if="auction?.me?.lots[lot.uuid].winner"
            class="btn btn-winner"
          >
            {{ $t("auction.winning") }}
          </a>
        </div>
      </div>
    </td>
  </tr>

  <BidModal
    v-if="authStore.canBid && biddingLot"
    :auction="auction"
    :lot="biddingLot"
    :my-lot="myLot"
    @close="biddingLot = null"
    @update-auction="$emit('update-auction', $event)"
  />

  <WarehousesModal
    v-if="warehousesModal"
    :lot="lot"
    @close="warehousesModal = false"
  />

  <AttributesModal
    v-if="attributesModal"
    :lot="lot"
    @close="attributesModal = false"
  />

  <HistoryModal
    v-if="historyModal"
    :lot="lot"
    :auction="auction"
    @close="historyModal = false"
  />
</template>

<script setup>
import stc from 'string-to-color';
import { ref } from 'vue'

import BidModal from '@/components/auction/BidModal.vue';
import CircleCountdown from '@/components/CircleCountdown.vue';
import AttributesButton from '@/components/lot/AttributesButton.vue';
import AttributesModal from '@/components/lot/AttributesModal.vue';
import BidButton from '@/components/lot/BidButton.vue';
import BidHistoryButton from '@/components/lot/BidHistoryButton.vue';
import BookmarkIcon from '@/components/lot/BookmarkIcon.vue';
import WarehouseButton from '@/components/lot/WarehouseButton.vue';
import WarehousesModal from '@/components/lot/WarehousesModal.vue';
import dateUtils from '@/services/date-utils';
import lotBookmark from '@/services/lot-bookmarks';
import productToColor from '@/services/product-to-color';
import { useAuthStore } from '@/stores/auth';

import HistoryModal from './HistoryModal.vue';

const emit = defineEmits(['update-auction'])

const props = defineProps({
    auction: {
        type: Object,
        required: true
    },
    lot: {
        type: Object,
        required: true
    },
    myLot: {
        type: Object,
        required: false,
        default: null
    },
    productMap: {
        type: Object,
        required: true
    },
    showIfBookmarked: {
        type: Boolean,
        required: true
    },
    isOutbid: {
        type: Boolean,
        required: false
    }
})

const authStore = useAuthStore();

const isBookmarked = ref(lotBookmark.isBookmarked(props.lot.uuid));

const biddingLot = ref(null)

const warehousesModal = ref(false);
const attributesModal = ref(false);
const historyModal = ref(false);
</script>